import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { HelperService } from '../services/helper.service';
import { Subscription } from 'rxjs';
import {
  LayoutOptimizationService,
  LayoutOptimizationType,
} from '../services/layout-optimization.service';

export enum ScrollPosition {
  None = 0,
  Top = 1,
  Median = 2,
  Bottom = 3,
}

export enum ScrollOverflow {
  Auto,
  X,
  Y,
}

@Component({
  standalone: false,
  selector: 'daui-scroll-viewer',
  templateUrl: './scroll-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./scroll-viewer.component.less'],
})
export class ScrollViewerComponent
  implements OnInit, OnDestroy, AfterContentInit, AfterViewInit, OnChanges
{
  scrollPosition = ScrollPosition;
  scrollOverflow = ScrollOverflow;
  currentScrollPosition = ScrollPosition.Top;
  startY: number;
  isDown = false;
  delta: number;
  prevScrollPosition = 0;
  prevCurrentScrollPosition = ScrollPosition.None;
  isLayoutOptimized = false;
  optimizationSubscribe: Subscription;
  isScrollBounce = false;
  timerBounce: ReturnType<typeof setTimeout>;

  constructor(
    private layoutOptimizationService: LayoutOptimizationService,
    private cdRef: ChangeDetectorRef,
    private element: ElementRef,
    private helperService: HelperService,
  ) {}

  @Input() hasLayoutOptimizationTrigger = true;
  @Input() hasIndicator = false;
  @Input() hasPullToRefresh = false;
  @Input() overflow: ScrollOverflow = ScrollOverflow.Y;
  @Input() scrollPositionY: number;
  @Input() isPullToRefreshCompleted = false;
  @Output() scrollPositionYChanged = new EventEmitter<number>();
  @Output() scrollPositionChanged = new EventEmitter<ScrollPosition>();
  @Output() pullToRefreshInProgress = new EventEmitter();

  @ViewChild('scroll', { static: false })
  scrollElement: ElementRef;

  onResize() {
    this.updateIndicator();
  }

  @HostListener('scroll', ['$event'])
  onElementScroll(_event: Event) {
    this.updateIndicator();
  }

  ngOnInit() {
    this.optimizationSubscribe = this.layoutOptimizationService
      .getOptimization()
      .subscribe((optimization) => {
        this.isLayoutOptimized = optimization !== LayoutOptimizationType.Nothing;
      });
  }
  ngOnDestroy(): void {
    if (this.optimizationSubscribe) {
      this.optimizationSubscribe.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    this.scrollElement.nativeElement.scrollTop = this.scrollPositionY;
    this.setScrollPositionY(this.scrollPositionY);

    if (this.hasLayoutOptimizationTrigger) {
      // Desactivate hasLayoutOptimizationTrigger if parent is daui-dialog
      this.hasLayoutOptimizationTrigger =
        this.helperService.getParentBySelector(this.element, 'daui-dialog') === null;
    }
  }

  ngAfterContentInit(): void {
    this.updateIndicator();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.scrollPositionY && this.scrollElement) {
      this.setScrollPositionY(changes.scrollPositionY.currentValue);
    }
    if (changes.isPullToRefreshCompleted && changes.isPullToRefreshCompleted.currentValue) {
      this.isPullToRefreshCompleted = true;
    }
  }
  setScrollPositionY(position: number) {
    this.scrollElement.nativeElement.scrollTop = position;
  }

  private updateIndicator() {
    if (!this.scrollElement) {
      return;
    }
    const scrollTop = this.scrollElement.nativeElement.scrollTop;
    const scrollHeight = this.scrollElement.nativeElement.scrollHeight;
    const offsetHeight = this.scrollElement.nativeElement.offsetHeight;
    this.currentScrollPosition = ScrollPosition.Median;

    if (
      this.scrollElement &&
      this.scrollElement.nativeElement &&
      this.scrollElement.nativeElement.scrollHeight > 0
    ) {
      this.cdRef.detectChanges();

      if (scrollTop < 1) {
        this.currentScrollPosition = ScrollPosition.Top;
      }
      if (offsetHeight + scrollTop > scrollHeight - 1) {
        this.currentScrollPosition = ScrollPosition.Bottom;
      }
      if (scrollTop < 1 && offsetHeight >= scrollHeight - 1) {
        this.currentScrollPosition = ScrollPosition.None;
      }
    }

    this.scrollPositionYChanged.emit(scrollTop);

    if (this.prevCurrentScrollPosition === this.currentScrollPosition) {
      if (this.prevCurrentScrollPosition === ScrollPosition.Median) {
        this.updateOptimization(scrollTop, offsetHeight, scrollHeight);
      }
      return;
    } else {
      this.prevCurrentScrollPosition = this.currentScrollPosition;
    }

    this.scrollPositionChanged.emit(this.currentScrollPosition);

    // stop reveive scroll position
    if (scrollTop < 0 || offsetHeight + scrollTop > scrollHeight - 1) {
      return;
    }
    this.updateOptimization(scrollTop, offsetHeight, scrollHeight);
  }
  updateOptimization(scrollTop: number, offsetHeight: number, scrollHeight: number) {
    if (this.hasLayoutOptimizationTrigger) {
      if (this.isScrollBounce) {
        return;
      }

      // optimize bounce Safari
      if (scrollTop < 0 || offsetHeight + scrollTop > scrollHeight) {
        this.isScrollBounce = true;
        if (scrollTop < 0) {
          this.prevScrollPosition = 0;
        }
        if (offsetHeight + scrollTop > scrollHeight) {
          this.prevScrollPosition = scrollHeight;
        }
        this.timerBounce = setTimeout(() => {
          this.scrollElement.nativeElement.scrollTop = this.prevScrollPosition;
          this.isScrollBounce = false;
        }, 2000);
        return;
      }

      if (scrollTop > this.prevScrollPosition && !this.isLayoutOptimized) {
        // console.log(
        //   'scrollTop > this.prevScrollPosition: ',
        //   scrollTop,
        //   ':',
        //   this.prevScrollPosition,
        //   ' - ',
        //   this.isLayoutOptimized
        // );
        this.layoutOptimizationService.optimize(LayoutOptimizationType.Full);
      }
      if (scrollTop < this.prevScrollPosition && this.isLayoutOptimized) {
        // console.log(
        //   'scrollTop < this.prevScrollPosition: ',
        //   scrollTop,
        //   ':',
        //   this.prevScrollPosition,
        //   ' - ',
        //   this.isLayoutOptimized
        // );
        this.layoutOptimizationService.optimize(LayoutOptimizationType.Nothing);
      }

      this.prevScrollPosition = scrollTop;
    }
  }
  onInProgress(isInProgress: boolean) {
    if (isInProgress) {
      this.pullToRefreshInProgress.emit();
    }
  }
}
